import React, { useState, useEffect } from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  VideoCameraIcon,
  MapPinIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Appointment, BookingSettings } from '../../types/appointments';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import { useLanguage } from '../../contexts/LanguageContext';

interface AppointmentEditModalProps {
  appointment: Appointment | null;
  settings: BookingSettings[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedAppointment: Appointment) => void;
}

const AppointmentEditModal: React.FC<AppointmentEditModalProps> = ({
  appointment,
  settings,
  isOpen,
  onClose,
  onSave
}) => {
  const { t } = useLanguage();
  
  // Form state
  const [formData, setFormData] = useState<Partial<Appointment>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when appointment changes
  useEffect(() => {
    if (appointment) {
      setFormData({
        clientName: appointment.clientName,
        clientEmail: appointment.clientEmail,
        clientPhone: appointment.clientPhone,
        appointmentDate: appointment.appointmentDate,
        appointmentTime: appointment.appointmentTime,
        duration: appointment.duration,
        status: appointment.status,
        clientMessage: appointment.clientMessage,
        meetingType: appointment.meetingType,
        meetingLink: appointment.meetingLink,
        meetingInstructions: appointment.meetingInstructions
      });
    }
  }, [appointment]);

  const handleInputChange = (field: keyof Appointment, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!appointment) return;

    setIsSubmitting(true);
    try {
      const updatedAppointment: Appointment = {
        ...appointment,
        ...formData,
        updatedAt: new Date()
      };
      
      await onSave(updatedAppointment);
      onClose();
    } catch (error) {
      console.error('Error updating appointment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSettingsTitle = (settingsId: string) => {
    const setting = settings.find(s => s.id === settingsId);
    return setting?.title || 'Ismeretlen szolgáltatás';
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const formatTime = (time: string) => {
    return time;
  };

  if (!appointment) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Időpont Szerkesztése"
      size="4xl"
    >
      <div className="p-6 space-y-6">
        {/* Szolgáltatás info - nem szerkeszthető */}
        <div className="bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-primary-200 dark:border-primary-700">
          <div className="flex items-center space-x-3">
            <CalendarDaysIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {getSettingsTitle(appointment.bookingSettingsId)}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Szolgáltatás típus
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bal oldal - Kliens adatok */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
              <UserIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <span>Kliens Adatok</span>
            </h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Név *
              </label>
              <input
                type="text"
                value={formData.clientName || ''}
                onChange={(e) => handleInputChange('clientName', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="Kliens neve"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email cím *
              </label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  value={formData.clientEmail || ''}
                  onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                  className="w-full pl-10 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Telefonszám
              </label>
              <div className="relative">
                <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="tel"
                  value={formData.clientPhone || ''}
                  onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                  className="w-full pl-10 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="+36 30 123 4567"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Üzenet
              </label>
              <div className="relative">
                <ChatBubbleLeftRightIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <textarea
                  value={formData.clientMessage || ''}
                  onChange={(e) => handleInputChange('clientMessage', e.target.value)}
                  rows={3}
                  className="w-full pl-10 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Opcionális üzenet..."
                />
              </div>
            </div>
          </div>

          {/* Jobb oldal - Időpont adatok */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
              <ClockIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <span>Időpont Részletek</span>
            </h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Dátum *
              </label>
              <input
                type="date"
                value={formData.appointmentDate ? formData.appointmentDate.toISOString().split('T')[0] : ''}
                onChange={(e) => handleInputChange('appointmentDate', new Date(e.target.value))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Időpont *
              </label>
              <input
                type="time"
                value={formData.appointmentTime || ''}
                onChange={(e) => handleInputChange('appointmentTime', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Időtartam (perc)
              </label>
              <input
                type="number"
                min="15"
                max="480"
                step="15"
                value={formData.duration || ''}
                onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="60"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Státusz
              </label>
              <select
                value={formData.status || ''}
                onChange={(e) => handleInputChange('status', e.target.value as Appointment['status'])}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="pending">Függőben</option>
                <option value="confirmed">Jóváhagyva</option>
                <option value="completed">Befejezve</option>
                <option value="cancelled">Lemondva</option>
              </select>
            </div>

            {/* Meeting típus és link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Meeting típus
              </label>
              <select
                value={formData.meetingType || 'in_person'}
                onChange={(e) => handleInputChange('meetingType', e.target.value as 'in_person' | 'online')}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="in_person">Személyes</option>
                <option value="online">Online</option>
              </select>
            </div>

            {formData.meetingType === 'online' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Meeting link
                  </label>
                  <div className="relative">
                    <VideoCameraIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="url"
                      value={formData.meetingLink || ''}
                      onChange={(e) => handleInputChange('meetingLink', e.target.value)}
                      className="w-full pl-10 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      placeholder="https://meet.google.com/..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Meeting instrukciók
                  </label>
                  <textarea
                    value={formData.meetingInstructions || ''}
                    onChange={(e) => handleInputChange('meetingInstructions', e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder="Csatlakozási instrukciók..."
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Akció gombok */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Mégse
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSubmitting || !formData.clientName || !formData.clientEmail}
            className="flex items-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Mentés...</span>
              </>
            ) : (
              <span>Mentés</span>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AppointmentEditModal;
